import { mapImageSources } from '../model/operations';

// Image files the creator picks: their pixel size (the asset request needs it) and, when a
// side exceeds `maxSide`, a downscaled JPEG/PNG copy so uploads stay light.
const decode = file => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => resolve({ image, url });
  image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Unreadable image')); };
  image.src = url;
});

export async function readImageFile(file) {
  const { image, url } = await decode(file);
  const size = { width: image.naturalWidth, height: image.naturalHeight };
  URL.revokeObjectURL(url);
  return size;
}

export async function prepareImageFile(file, { maxSide = 2400, quality = 0.88 } = {}) {
  const { image, url } = await decode(file);
  const width = image.naturalWidth;
  const height = image.naturalHeight;
  const scale = Math.min(1, maxSide / Math.max(width, height));
  if (scale === 1) { URL.revokeObjectURL(url); return { blob: file, width, height, mimeType: file.type }; }
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
  URL.revokeObjectURL(url);
  const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const blob = await new Promise(resolve => canvas.toBlob(resolve, mimeType, quality));
  return { blob: blob || file, width: canvas.width, height: canvas.height, mimeType: blob ? mimeType : file.type };
}

export const blobToDataUrl = blob => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
// A downloaded image as a file the upload takes: an unknown type (a bucket answering
// octet-stream) is drawn again as PNG.
async function asImageFile(blob, name) {
  if (IMAGE_TYPES.includes(blob.type)) return new File([blob], name, { type: blob.type });
  const { image, url } = await decode(blob);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  canvas.getContext('2d').drawImage(image, 0, 0);
  URL.revokeObjectURL(url);
  const png = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  if (!png) throw new Error('Unreadable image');
  return new File([png], name, { type: 'image/png' });
}

// A template brought from elsewhere keeps nobody else's images: each uploaded (`asset`) image
// is downloaded from its URL and uploaded again through the host's `upload(file)` (which
// answers `{ id, url, width, height }`), once per image however many layers show it.
// `library` images are Ginastee's and stay. When any image cannot be copied (an expired or
// blocked link), it rejects with `code: 'image_copy'` and the `count` that failed.
export async function copyDocumentImages(doc, upload, { fetcher = (...args) => fetch(...args) } = {}) {
  const keyOf = source => source.asset_id || source.url;
  const sources = new Map();
  mapImageSources(doc, source => { if (source?.kind === 'asset') sources.set(keyOf(source), source); return source; });
  const copies = new Map();
  let failed = 0;
  for (const [key, source] of sources) {
    try {
      if (!source.url) throw new Error('No URL to copy from');
      const response = await fetcher(source.url, { mode: 'cors', credentials: 'omit' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const image = await upload(await asImageFile(await response.blob(), `${source.asset_id || 'image'}.png`));
      copies.set(key, { kind: 'asset', asset_id: image.id, url: image.url, width: image.width, height: image.height });
    } catch { failed += 1; }
  }
  if (failed) throw Object.assign(new Error('Template images could not be copied'), { code: 'image_copy', count: failed });
  return mapImageSources(doc, source => (source?.kind === 'asset' ? copies.get(keyOf(source)) : source));
}
